import { ClientTourismEvent } from '@/tourism-events/tourism-events.model';
import { OrganizationModel } from '@/organization/organization/organization.model';
import { EventCategory } from '@/common/enums/EventCategory';
import { BagApiResponse } from '@/common/models/response.model';
import {
	Convenience,
	Product,
	SurchargeBoard,
	SurchargeProgram,
	Tag,
	Location,
	Promotion,
} from '@/reservation/reservation.model';
import { TransportLogic } from '@/new-transport/transport-map/transport-map.api.service';
import { TransportTimetable } from '@/new-transport/_models/transport-timetable.model';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { components } from '@/common/bag-api';
import { CamelCaseKeys } from '@/common/types/snake-to-camel-case';
import { ComfortSeatConflict } from '@/new-transport/_models/transport.model';

export interface TransportTermData {
	id: number;
	transportStop: TransportStop;
	name: string;
	departureTime: string;
	departure: string;
	accommodation: Accommodation;
	accommodationFrom: string;
	returnTime: string;
	status: number;
	offerTerm: number[];
	departureMaxPlaces: number;
	returnMaxPlaces: number;
	swinging: boolean;
	actionTerm: boolean;
	transportDeparture: TransportDeparture;
	transportReturn: TransportReturn;
	stopSale: boolean;
	markAsPopular: boolean;
	maxSeatOptional: number;
	sumOfTransport: SumOfTransport;
	product: string;
	termtourismeventSet: TermTourismEventSet[];
	highlightLastSeats: boolean;
	organizationStatus: CamelCaseKeys<components['schemas']['TransportTermOrganizationStatus']>;
	comfortPackagePool: number | null;
	comfortPackagePoolTerms: ComfortPackagePoolTerm[];
}

export interface ComfortPackagePoolTerm {
	id: number;
	name: string;
}

export interface ComfortPackagePoolLinkResponse {
	comfortPackagePool: number;
}

export interface ComfortPackagePoolConflictsError {
	conflicts: ComfortSeatConflict[];
}

export const COMFORT_PACKAGE_POOL_CONFLICTS_ERROR = '"conflicts":[{"reservationId"';

export type TransportTermListAddational = Omit<TransportTermData, 'stopSale' | 'swinging'>;

export interface TransportTermList extends Partial<TransportTermListAddational> {
	stopSale: string;
	swinging: string;
	kind: OfferTransportKind;
	departureTimeDay: string;
	returnTimeDay: string;
	touristGuides: string[];
	color: string;
	checked: boolean;
	blockedTimetableCount?: number;
	owuConfirmationDate?: string;
	comfortPackagePoolTooltip?: string;
}

export interface EventsGuides {
	event: string;
	name: string;
}

export interface TourismEvents {
	id: number;
	crewCount: number;
	eventId: string;
	kind: string;
	maxSeat: number;
}

//adding new property to TourismEvents breaks too many things, so just making a new type
export interface TourismEventWithSlots extends TourismEvents {
	slotsCount: TourismEventSlots;
}

export interface TourismEventSlots {
	departureOk: number;
	departureOp: number;
	returnOk: number;
	returnOp: number;
}

export interface Accommodation extends AccommodationAddational {
	lower: string;
	upper: string;
	bounds: string;
}

export interface AccommodationAddational {
	lowerDay: string;
	upperDay: string;
}

export interface TransportStop {
	id: number;
	parentStop: number;
	location: number;
	address: string;
	stopType: string;
	erpGrid: number[];
	longitude: string;
	latitude: string;
}

export interface TransportDeparture {
	id: number;
	kind: string;
	linkedTransport: TransportDeparture;
}

export interface TransportReturn {
	id: number;
	kind: string;
	linkedTransport: TransportReturn;
}

export interface TermTourismEventSet {
	id: number;
	eventId: string;
	kind: TermTourismEventSetKinds;
	maxSeat: number;
	crewCount: number;
	eventName?: string;
}

export enum TermTourismEventSetKinds {
	DEPARTURE = 'D',
	RETURN = 'R',
	BOTH = 'B',
}

export interface SumOfTransport {
	reservations: ReservationsCountTransport;
	slots: SlotsCountTransport;
}

export interface ReservationsCountTransport extends AddationalCountTransport {
	departureReservationCount: number;
	departureReservationCountOptional: number;
	returnReservationCount: number;
	returnReservationCountOptional: number;
}

export interface SlotsCountTransport extends AddationalCountTransport {
	departureSlotsCount: number;
	departureSlotsCountOptional: number;
	returnSlotsCount: number;
	returnSlotsCountOptional: number;
}

export interface AddationalCountTransport {
	returnConfirm: number;
	departureConfirm: number;
}

export interface BagTransportTerm {
	accommodation: Accommodation;
	actionTerm: boolean;
	color: string;
	departureMaxPlaces: number;
	departureTime: string;
	departureTimeDay: string;
	id: number;
	maxSeatOptionalDeparture: number;
	maxSeatOptionalReturn: number;
	name: string;
	offerTerm: number[];
	products: number[];
	reservations: BagTransportTermReservations;
	returnMaxPlaces: number;
	returnTime: string;
	returnTimeDay: string;
	slots: BagTransportTermSlots;
	status: number;
	stopSale: boolean;
	markAsPopular: boolean;
	sumOfTransport: BagTransportSumOfTransport;
	swinging: boolean;
	termtourismeventSet: TermTourismEventSet[];
	touristGuides: string[];
	transportDeparture: TransportDeparture;
	transportReturn: TransportReturn;
	transportStop: TransportStop;
	insurers: string[];
	peopleWithFaculties: number;
	notesCount: number;
	files: any[];
	responsibleUser: {
		id: number;
		firstName: string;
		lastName: string;
	};
}

export interface BagTransportTermReservations {
	departureReservationCount: number;
	departureReservationCountOptional: number;
	departureConfirm: number;
	returnReservationCount: number;
	returnReservationCountOptional: number;
	returnConfirm: number;
}

export interface BagTransportTermSlots {
	departureSlotsCount: number;
	departureSlotsCountOptional: number;
	departureConfirm: number;
	returnSlotsCount: number;
	returnSlotsCountOptional: number;
	returnConfirm: number;
}

export interface BagTransportSumOfTransport {
	reservations: BagTransportSumOfTransportReservations;
	slots: BagTransportSumOfTransportSlots;
}

export type BagTransportSumOfTransportReservations = Omit<
	BagTransportTermReservations,
	'returnConfirm' | 'departureConfirm'
>;
export type BagTransportSumOfTransportSlots = Omit<BagTransportTermSlots, 'returnConfirm' | 'departureConfirm'>;

export interface GenerateReportPayload {
	version?: 'v1';
	name?: string;
	params: {
		term_id: string;
		term?: {
			lower?: string;
			upper?: string;
			bounds: string;
		};
		product?: number;
	};
	insurer?: string;
	output_params:
		| {
				name: string;
				sheetName: string;
				path: string;
				kind: 'SpreadSheet';
		  }
		| {
				name: string;
				sheetName: string;
				path: string;
				kind: 'Google Drive';
		  }
		| {
				name: string;
				kind: 'CSV';
		  };
}

export interface VehicleModel {
	bagId: number;
	busType: {
		id: number;
		name: string;
	};
	busType_name: string;
	cmaDevice: string;
	confirmDate: string;
	gbox_id: number;
	id: string;
	idShort: string;
	lastFiltersChange: string;
	lastOilChange: string;
	mileage: number;
	name: string;
	nawigatorId: number;
	nawigatorStatus: string;
	oilChangeFrequency: number;
	premium: string;
	registrationNumber: string;
	seats: number;
	status: VehicleStatus;
	status_name: string;
	tiresType: string;
	type: VehicleType;
	vehicleMark: string;
	vehicleModel: string;
	vinNumber: string;
	wrappingType: number;
	yearOfProduction: number;
}

export interface VehicleStatus {
	id: number;
	name: string;
}

export interface VehicleType {
	id: number;
	name: string;
}

export interface BagTransportTermApiResponse {
	data: any;
	error: any;
	errorCode: number;
}

export interface TourismEventsWithDelay {
	accommodation: string;
	accommodation_detail: string;
	agreement_mileage_limit: number;
	agreement_signature: string;
	agreement_value: number;
	author: number;
	board: number;
	board_confirmed: boolean;
	briefingPlace: string;
	briefingUser: string;
	calculation_link: string;
	category: EventCategory;
	clientEvent: ClientTourismEvent;
	client_representative: string;
	client_wishes: string;
	cmaCollision: boolean;
	cmaUpdate: string;
	countCrewToSeason: number;
	created: string;
	dateDeadlineAuto: boolean;
	datePrepareAuto: boolean;
	date_deadline: string;
	date_from: string;
	date_prepare: string;
	date_to: string;
	deleted: string;
	departure_place_confirmed: boolean;
	detail_about_first_day_confirmed: boolean;
	detail_about_last_day_confirmed: boolean;
	detail_about_program_confirmed: boolean;
	deviceStatistic: string;
	driverInformedDepartureHistoryLast: DriverInformedHistory;
	driverInformedReturnHistoryLast: DriverInformedHistory;
	guideInformedHistoryLast: DriverInformedHistory;
	clientInformedHistoryLast: DriverInformedHistory;
	readyForPrintingHistoryLast: DriverInformedHistory;
	driver_informed: boolean;
	driver_informed_departure: boolean;
	driver_informed_return: boolean;
	drivers_departure: DriversInformation[];
	drivers_program: DriversInformation[];
	drivers_return: DriversInformation[];
	drivers_senior: DriversInformation[];
	endDate: string;
	endPlace: string;
	endPlaceCity: string;
	endPostCode: string;
	errorsCount: number;
	guideInformedReturnHistoryLast: number;
	guide_informed: boolean;
	id: number;
	is_virtual_driver: boolean;
	leadSource: number;
	leftToPay: number;
	name: string;
	newId: number;
	night: boolean;
	night_confirmed: boolean;
	note: string;
	orderNumber: number;
	organizationOwner: Array<OrganizationModel>;
	owner: Array<string>;
	people: number;
	people_confirmed: boolean;
	place_return: string;
	planned_people_count: number;
	program_date_from: string;
	program_date_to: string;
	return_place_confirmed: boolean;
	season: Array<any>;
	seasonEvents: Array<any>;
	services: boolean;
	services_confirmed: boolean;
	startDate: string;
	startPlace: string;
	startPlaceCity: string;
	startPostCode: string;
	status: string;
	tasks: Array<any>;
	textBriefing: string;
	text_billing: string;
	text_client: string;
	text_departure: string;
	text_drivers: string;
	text_guide: string;
	text_hotel: string;
	text_notes: string;
	text_opinion: string;
	text_organization: string;
	text_program: string;
	text_refueling: string;
	text_return: string;
	tourist_guides_departure: Array<any>;
	tourist_guides_program: Array<any>;
	tourist_guides_return: Array<any>;
	vehicles: TourismEventsVehicle[];
}

interface TourismEventsVehicle {
	date_start: string;
	date_stop: string;
	id: number;
	vehicle_id: string;
}

export interface DriverInformedHistory {
	date: string;
	event: string;
	type: string;
	user: string;
	value: boolean;
}

export interface DriversInformation {
	confirmed: boolean;
	dateFromAuto: boolean;
	dateToAuto: boolean;
	date_from: string;
	date_to: string;
	driver: string;
	event: string;
	name: string;
	status: DriversProgramStatus;
	tourismEventDriver: number;
	type: string;
}

export enum DriversProgramStatus {
	REGULAR = 'REGULAR',
	JUMPER = 'JUMPER',
	B2B = 'B2B',
}

export interface Report {
	id: number;
	name: string;
	outputParams: ReportOutputParams;
	params: ReportParams;
	version: string;
}

export interface ReportOutputParams {
	name: string;
	kind: string;
}

export interface ReportParams {
	product: string;
	term: ReportParamsTerm;
	termId: string;
}

export interface ReportParamsTerm {
	lower: string;
	upper: string;
	bounds: string;
}
export interface GeneratedReport {
	completedAt: string;
	name: string;
	params: ReportParams;
	status: string;
	url: string;
	version: string;
}

export interface BagTransportTermProduct {
	address: string;
	board: number;
	buildingNumber: string;
	code: string;
	conveniences: number[];
	descriptions: BagTransportTermProductDescription[];
	id: number;
	latitude: string;
	longitude: string;
	location: number[];
	name: string;
	nightsStay: number;
	organization: number;
	organizationNote: string;
	priceSetting: number;
	productKind: number;
	productMultimedias: BagTransportTermProductMultimedia[];
	rooms: BagTransportTermProductRoom[];
	stars: number;
	status: string;
	stop: number;
	surchargeBoard: SurchargeBoard;
	surchargeProgram: SurchargeProgram;
	tags: Tag[];
	zipCode: string;
}

export interface BagTransportTermProductDescription {
	description: string;
	descriptionHeader: number;
	id: number;
}

export interface BagTransportTermProductMultimedia {
	id: number;
	multimedia: Multimedia;
	order: number;
}

export interface Multimedia {
	id: string;
	name: string;
	url: string;
}

export interface BagTransportTermProductRoom {
	conveniences: Convenience[];
	description: string;
	fee: number;
	id: number;
	isActive: boolean;
	isNoName: boolean;
	level: number;
	order: number;
	roomNumber: string;
	roomSlaves: RoomSlave[];
	roomType: RoomType;
	view: string;
}

export interface RoomType {
	id: number;
	configurations: RoomTypeConfiguration[];
	code: string;
	kind: string;
	name: string;
}

export interface RoomTypeConfiguration {
	id: number;
	kind: string;
	count: number;
	maxAge: number;
}

export interface RoomSlave {
	fee: number;
	id: number;
	roomType: RoomType;
}

export enum ProductKindType {
	ROUND_TRIP = 2,
}

export interface ProductRoomsAvailabilities {
	roomTypes: RoomAvailability[];
	sumOfRoom: number;
	sumOfConfirmedOffers: number;
	sumOfOptionOffers: number;
}

export interface RoomAvailability {
	id: number;
	name: string;
	code: string;
	available: number;
	count: number;
	confirmedOffersCount: number;
	optionOffersCount: number;
	mode?: string;
}

export interface ProductRoomAvailability {
	roomType: number;
	term: number;
	expectedAvailabilities: number;
}

export enum AvailabilityRoomStatus {
	DISABLED = 'disabled',
	EDIT = 'edit',
	SAVE = 'save',
}

export interface GeneralRoomAvailability {
	sumOfConfirmedOffers: number;
	sumOfOptionOffers: number;
	sumOfAllReservations: number;
}

export interface OfferTerm {
	archived: boolean;
	dateRange: {
		lower: string;
		upper: string;
		bounds: string;
	};
	id: number;
	locations: Location[];
	maxRooms: number;
	merlinx: boolean;
	offerName: string;
	pricePriority: string;
	priceSettings: string;
	product: Product;
	promotion: boolean;
	promotions: Promotion[];
	sale: boolean;
	status: number;
	stopSale: boolean;
	termPrice: OfferTermPrice;
	www: boolean;
}

export interface OfferTermPrice {
	id: number;
	price: number;
	status: string;
	dateFrom: string;
}

export interface OfferTermMaxRooms {
	maxRooms: number;
	mode?: MaxRoomStatus;
}

export enum MaxRoomStatus {
	EDIT = 'edit',
	SAVE = 'save',
}
export type BagTransportTransportLogicResult = BagApiResponse<TransportLogic>;
export type BagTransportTransportTimetableResult = BagApiResponse<TransportTimetable>;
